import {
  DeliveryShipmentManifestReadinessIssueSchema,
  ShipmentManifestPayloadSnapshotSchema
} from "../chunk-3XEHZB66.js";

// compliance/license.schema.ts
import { z } from "zod";
var LICENSE_TYPES = [
  "cultivator",
  "dispensary",
  "manufacturer",
  "testing_lab",
  "transporter",
  "combined_use"
];
var LicenseTypeSchema = z.enum(LICENSE_TYPES);
var LICENSE_STATUSES = [
  "active",
  "expired",
  "suspended",
  "revoked",
  "pending_verification"
];
var LicenseStatusSchema = z.enum(LICENSE_STATUSES);
var LicenseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  licenseNumber: z.string(),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema,
  issuingState: z.string(),
  issuingAuthority: z.string().nullable(),
  issuedDate: z.date().nullable(),
  expirationDate: z.date().nullable(),
  lastVerifiedAt: z.date().nullable(),
  verifiedBy: z.string().uuid().nullable(),
  premisesName: z.string().nullable(),
  premisesAddress1: z.string(),
  premisesAddress2: z.string().nullable(),
  premisesCity: z.string(),
  premisesProvince: z.string(),
  premisesPostalCode: z.string(),
  premisesCountryCode: z.string(),
  premisesLatitude: z.number().nullable(),
  premisesLongitude: z.number().nullable(),
  premisesGeocodedAt: z.date().nullable(),
  stockLocationId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable()
});
var LicenseDtoSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  licenseNumber: z.string(),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema,
  issuingState: z.string(),
  issuingAuthority: z.string().nullable(),
  issuedDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
  verifiedBy: z.string().nullable(),
  premisesName: z.string().nullable(),
  premisesAddress1: z.string(),
  premisesAddress2: z.string().nullable(),
  premisesCity: z.string(),
  premisesProvince: z.string(),
  premisesPostalCode: z.string(),
  premisesCountryCode: z.string(),
  premisesLatitude: z.number().nullable(),
  premisesLongitude: z.number().nullable(),
  premisesGeocodedAt: z.string().datetime().nullable(),
  stockLocationId: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var LicenseUpsertFieldsSchema = z.object({
  organizationId: z.string().uuid(),
  licenseNumber: z.string().min(1),
  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema.optional(),
  issuingState: z.string().optional(),
  issuingAuthority: z.string().nullable().optional(),
  issuedDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  premisesName: z.string().nullable().optional(),
  premisesAddress1: z.string().min(1),
  premisesAddress2: z.string().nullable().optional(),
  premisesCity: z.string().min(1),
  premisesProvince: z.string().min(1),
  premisesPostalCode: z.string().min(1),
  premisesCountryCode: z.string().optional(),
  stockLocationId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var CreateLicenseDtoSchema = LicenseUpsertFieldsSchema;
var UpdateLicenseDtoSchema = LicenseUpsertFieldsSchema.partial();
var LicenseFiltersSchema = z.object({
  organizationId: z.string().uuid().optional(),
  status: LicenseStatusSchema.optional(),
  licenseType: LicenseTypeSchema.optional()
});

// compliance/transfer-template.schema.ts
import { z as z5 } from "zod";

// compliance/transfer-template-segment.schema.ts
import { z as z2 } from "zod";
var TransferTemplateSegmentSchema = z2.object({
  id: z2.string().uuid(),
  templateId: z2.string().uuid(),
  segmentIndex: z2.number().int().nonnegative(),
  driverId: z2.string().uuid(),
  vehicleIdOverride: z2.string().uuid().nullable(),
  estimatedDepartureAt: z2.string().min(1),
  estimatedArrivalAt: z2.string().min(1),
  createdAt: z2.date(),
  updatedAt: z2.date()
});
var TransferTemplateSegmentInputSchema = z2.object({
  driverId: z2.string().uuid(),
  vehicleIdOverride: z2.string().uuid().optional(),
  estimatedDepartureAt: z2.string().min(1),
  estimatedArrivalAt: z2.string().min(1)
});
var CreateTransferTemplateSegmentSchema = z2.object({
  templateId: z2.string().uuid(),
  segmentIndex: z2.number().int().nonnegative(),
  driverId: z2.string().uuid(),
  vehicleIdOverride: z2.string().uuid().nullable().optional(),
  estimatedDepartureAt: z2.string().min(1),
  estimatedArrivalAt: z2.string().min(1)
});
var TransferTemplateSegmentDtoSchema = z2.object({
  id: z2.string().uuid(),
  templateId: z2.string().uuid(),
  segmentIndex: z2.number().int().nonnegative(),
  driverId: z2.string().uuid(),
  vehicleIdOverride: z2.string().uuid().nullable(),
  estimatedDepartureAt: z2.string(),
  estimatedArrivalAt: z2.string(),
  createdAt: z2.string(),
  updatedAt: z2.string()
});

// compliance/transfer-template-handoff.schema.ts
import { z as z3 } from "zod";
var TransferTemplateHandoffSchema = z3.object({
  id: z3.string().uuid(),
  templateId: z3.string().uuid(),
  afterSegmentIndex: z3.number().int().nonnegative(),
  licenseId: z3.string().uuid().nullable(),
  locationName: z3.string().nullable(),
  addressLine1: z3.string().nullable(),
  addressCity: z3.string().nullable(),
  addressProvince: z3.string().nullable(),
  addressPostalCode: z3.string().nullable(),
  notes: z3.string().nullable(),
  createdAt: z3.date(),
  updatedAt: z3.date()
});
var TransferTemplateHandoffInputSchema = z3.object({
  afterSegmentIndex: z3.number().int().nonnegative(),
  licenseId: z3.string().uuid().optional(),
  locationName: z3.string().min(1).optional(),
  addressLine1: z3.string().min(1).optional(),
  addressCity: z3.string().min(1).optional(),
  addressProvince: z3.string().min(1).optional(),
  addressPostalCode: z3.string().min(1).optional(),
  notes: z3.string().max(1e3).optional()
}).refine(
  (h) => Boolean(h.licenseId) || Boolean(h.locationName),
  { message: "handoff must provide either licenseId or locationName" }
);
var CreateTransferTemplateHandoffSchema = z3.object({
  templateId: z3.string().uuid(),
  afterSegmentIndex: z3.number().int().nonnegative(),
  licenseId: z3.string().uuid().nullable().optional(),
  locationName: z3.string().nullable().optional(),
  addressLine1: z3.string().nullable().optional(),
  addressCity: z3.string().nullable().optional(),
  addressProvince: z3.string().nullable().optional(),
  addressPostalCode: z3.string().nullable().optional(),
  notes: z3.string().nullable().optional()
});
var TransferTemplateHandoffDtoSchema = z3.object({
  id: z3.string().uuid(),
  templateId: z3.string().uuid(),
  afterSegmentIndex: z3.number().int().nonnegative(),
  licenseId: z3.string().uuid().nullable(),
  locationName: z3.string().nullable(),
  addressLine1: z3.string().nullable(),
  addressCity: z3.string().nullable(),
  addressProvince: z3.string().nullable(),
  addressPostalCode: z3.string().nullable(),
  notes: z3.string().nullable(),
  createdAt: z3.string(),
  updatedAt: z3.string()
});

// compliance/transfer-template-layover.schema.ts
import { z as z4 } from "zod";
var LAYOVER_ANCHOR_TYPES = ["at_handoff", "mid_segment"];
var LayoverAnchorTypeSchema = z4.enum(LAYOVER_ANCHOR_TYPES);
var TransferTemplateLayoverSchema = z4.object({
  id: z4.string().uuid(),
  templateId: z4.string().uuid(),
  anchorType: LayoverAnchorTypeSchema,
  anchorSegmentIndex: z4.number().int().nonnegative(),
  licenseId: z4.string().uuid(),
  checkInAt: z4.string().min(1),
  checkOutAt: z4.string().min(1),
  createdAt: z4.date(),
  updatedAt: z4.date()
});
var TransferTemplateLayoverInputSchema = z4.object({
  anchorType: LayoverAnchorTypeSchema,
  anchorSegmentIndex: z4.number().int().nonnegative(),
  licenseId: z4.string().uuid(),
  checkInAt: z4.string().min(1),
  checkOutAt: z4.string().min(1)
});
var CreateTransferTemplateLayoverSchema = z4.object({
  templateId: z4.string().uuid(),
  anchorType: LayoverAnchorTypeSchema,
  anchorSegmentIndex: z4.number().int().nonnegative(),
  licenseId: z4.string().uuid(),
  checkInAt: z4.string().min(1),
  checkOutAt: z4.string().min(1)
});
var TransferTemplateLayoverDtoSchema = z4.object({
  id: z4.string().uuid(),
  templateId: z4.string().uuid(),
  anchorType: LayoverAnchorTypeSchema,
  anchorSegmentIndex: z4.number().int().nonnegative(),
  licenseId: z4.string().uuid(),
  checkInAt: z4.string(),
  checkOutAt: z4.string(),
  createdAt: z4.string(),
  updatedAt: z4.string()
});

// compliance/transfer-template.schema.ts
var TRANSFER_TEMPLATE_STATUSES = ["draft", "ready", "submitted", "voided"];
var TransferTemplateStatusSchema = z5.enum(TRANSFER_TEMPLATE_STATUSES);
var TransferTemplateReadinessSchema = z5.object({
  isReady: z5.boolean(),
  blockingErrors: z5.array(DeliveryShipmentManifestReadinessIssueSchema),
  warnings: z5.array(DeliveryShipmentManifestReadinessIssueSchema),
  info: z5.array(DeliveryShipmentManifestReadinessIssueSchema).default([]),
  checkedAt: z5.date(),
  metadata: z5.record(z5.unknown()).default({})
});
var TransferTemplateSchema = z5.object({
  id: z5.string().uuid(),
  partitionId: z5.string().uuid(),
  organizationId: z5.string().uuid(),
  status: TransferTemplateStatusSchema,
  version: z5.number().int().positive(),
  metrcTransferId: z5.number().int().positive().nullable(),
  metrcManifestNumber: z5.string().nullable(),
  shipperLicenseNumber: z5.string().nullable(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.nullable(),
  lastPreparedAt: z5.date().nullable(),
  submittedAt: z5.date().nullable(),
  voidedAt: z5.date().nullable(),
  // KEEP until migration 164 — retired in plan transfer-template-layover-driver-separation.md
  metadata: z5.record(z5.unknown()),
  createdAt: z5.date(),
  updatedAt: z5.date()
});
var CreateTransferTemplateSchema = z5.object({
  partitionId: z5.string().uuid(),
  organizationId: z5.string().uuid(),
  status: TransferTemplateStatusSchema.default("draft"),
  version: z5.number().int().positive().default(1),
  metrcTransferId: z5.number().int().positive().nullable().optional(),
  metrcManifestNumber: z5.string().nullable().optional(),
  shipperLicenseNumber: z5.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.nullable().optional(),
  lastPreparedAt: z5.date().nullable().optional(),
  submittedAt: z5.date().nullable().optional(),
  voidedAt: z5.date().nullable().optional(),
  metadata: z5.record(z5.unknown()).optional()
});
var UpdateTransferTemplateSchema = z5.object({
  status: TransferTemplateStatusSchema.optional(),
  version: z5.number().int().positive().optional(),
  metrcTransferId: z5.number().int().positive().nullable().optional(),
  metrcManifestNumber: z5.string().nullable().optional(),
  shipperLicenseNumber: z5.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema.optional(),
  readinessSnapshot: TransferTemplateReadinessSchema.nullable().optional(),
  lastPreparedAt: z5.date().nullable().optional(),
  submittedAt: z5.date().nullable().optional(),
  voidedAt: z5.date().nullable().optional(),
  metadata: z5.record(z5.unknown()).optional()
});
var TransferTemplateDtoSchema = z5.object({
  id: z5.string().uuid(),
  partitionId: z5.string().uuid(),
  organizationId: z5.string().uuid(),
  status: TransferTemplateStatusSchema,
  version: z5.number().int().positive(),
  metrcTransferId: z5.number().int().positive().nullable(),
  metrcManifestNumber: z5.string().nullable(),
  shipperLicenseNumber: z5.string().nullable(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: TransferTemplateReadinessSchema.extend({
    checkedAt: z5.string()
  }).nullable(),
  lastPreparedAt: z5.string().nullable(),
  submittedAt: z5.string().nullable(),
  voidedAt: z5.string().nullable(),
  metadata: z5.record(z5.unknown()),
  createdAt: z5.string(),
  updatedAt: z5.string()
});
var TransferTemplateReadinessDtoSchema = TransferTemplateReadinessSchema.extend({
  checkedAt: z5.string()
});
var TransferTemplateBatchStatusItemSchema = z5.object({
  partitionId: z5.string().uuid(),
  status: TransferTemplateStatusSchema,
  driverName: z5.string().nullable(),
  metrcManifestNumber: z5.string().nullable(),
  submittedAt: z5.string().nullable(),
  estimatedDepartureAt: z5.string().nullable()
});
var PrepareTransferTemplateRequestSchema = z5.object({
  manifestName: z5.string().min(1).optional(),
  transferTypeName: z5.string().min(1).optional(),
  plannedRoute: z5.string().min(1).optional(),
  segments: z5.array(TransferTemplateSegmentInputSchema).min(1),
  handoffs: z5.array(TransferTemplateHandoffInputSchema).optional(),
  layover: TransferTemplateLayoverInputSchema.optional()
});
export {
  CreateLicenseDtoSchema,
  CreateTransferTemplateHandoffSchema,
  CreateTransferTemplateLayoverSchema,
  CreateTransferTemplateSchema,
  CreateTransferTemplateSegmentSchema,
  LAYOVER_ANCHOR_TYPES,
  LICENSE_STATUSES,
  LICENSE_TYPES,
  LayoverAnchorTypeSchema,
  LicenseDtoSchema,
  LicenseFiltersSchema,
  LicenseSchema,
  LicenseStatusSchema,
  LicenseTypeSchema,
  PrepareTransferTemplateRequestSchema,
  TRANSFER_TEMPLATE_STATUSES,
  TransferTemplateBatchStatusItemSchema,
  TransferTemplateDtoSchema,
  TransferTemplateHandoffDtoSchema,
  TransferTemplateHandoffInputSchema,
  TransferTemplateHandoffSchema,
  TransferTemplateLayoverDtoSchema,
  TransferTemplateLayoverInputSchema,
  TransferTemplateLayoverSchema,
  TransferTemplateReadinessDtoSchema,
  TransferTemplateReadinessSchema,
  TransferTemplateSchema,
  TransferTemplateSegmentDtoSchema,
  TransferTemplateSegmentInputSchema,
  TransferTemplateSegmentSchema,
  TransferTemplateStatusSchema,
  UpdateLicenseDtoSchema,
  UpdateTransferTemplateSchema
};
